//AllApplications
import { Button, Form, Pagination, Table } from "react-bootstrap"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useDispatch, useSelector } from "react-redux"
import { camelCaseToWords, downloadFile, formatDate } from "../../../constant/utils"
import { css } from "@emotion/react"
import { downloadQuotePDF, getApplications, searchApplications } from "../../../data/applicationSlice"
import Loader from "../../../components/Loader"
import { useState } from "react"
import { byPrefixAndName } from "@awesome.me/kit-de80aecb6a/icons"

const componentStyle = css`
    p.note {
        color: #333;
        background: gold;
        font-size: 14px;
        margin: 12px 0 0 0;
    }
`

const AllApplications = ({
    handleInformationShow,
    handleOpenModal,
    handleComments,
    handleNavigatorState,
    handleBORModal
}) => {
    const dispatch = useDispatch()
    const { data: applicationList, fetchStatus, totalRecords } = useSelector((state) => state.application)
    const { user, role } = useSelector((state) => state.authentication)
    const [activePage, setActivePage] = useState(0)
    const [isSearched, setIsSearched] = useState(false)
    const [search, setSearch] = useState("")

    const numberOfRecordsPerPage = 25
    const totalPages = Math.ceil(totalRecords / numberOfRecordsPerPage)

    // Function to handle page change
    const handlePageChange = (index) => {
        if (index >= 0) {
            if (index === activePage) return
            setActivePage(index)
            if (isSearched) {
                handleSearch(index + 1)
            } else {
                getApplicationsList(index + 1, true)
            }
        }
    }

    const getApplicationsList = (pageIndex = 1, isPaginationHappening = false) => {
        if (isSearched) {
            setIsSearched(false)
            setSearch("")
        }
        if (!isPaginationHappening) setActivePage(0)
        return dispatch(
            getApplications({
                pageIndex,
                numberOfRecords: numberOfRecordsPerPage,
                orderBy: "firstNamedInsured",
                descending: true
            })
        )
    }

    const handleSearch = (pageIndex = 1) => {
        dispatch(
            searchApplications({
                searchTerm: search,
                pageIndex,
                numberOfRecords: numberOfRecordsPerPage,
                orderBy: "firstNamedInsured",
                descending: true
            })
        ).then(() => {
            if (!isSearched) setIsSearched(true)
        })
    }

    const downloadApplicationQuote = (id) => {
        dispatch(downloadQuotePDF(id)).then(({ payload }) => {
            if (payload?.file) {
                downloadFile(payload)
            }
        })
    }

    const renderPagination = () => {
        const paginationItems = []
        const ellipsis = (
            <Pagination.Ellipsis
                key="ellipsis"
                disabled
            />
        )
        const pagesToShow = 5 // Number of pages to show in the middle
        const tailPagesToShow = 2 // Number of pages to show at the end

        // Add First and Previous Buttons
        paginationItems.push(
            <Pagination.First
                key="first"
                onClick={() => handlePageChange(0)}
                disabled={activePage === 0}
            />
        )
        paginationItems.push(
            <Pagination.Prev
                key="prev"
                onClick={() => handlePageChange(activePage - 1)}
                disabled={activePage === 0}
            />
        )

        // Add initial pages or ellipsis
        if (activePage > pagesToShow - 1) {
            paginationItems.push(
                <Pagination.Item
                    key={0}
                    onClick={() => handlePageChange(0)}
                    active={activePage === 0}>
                    {1}
                </Pagination.Item>
            )
            paginationItems.push(ellipsis)
        }

        // Add middle pages
        const startPage = Math.max(0, activePage - Math.floor(pagesToShow / 2))
        const endPage = Math.min(totalPages, startPage + pagesToShow)

        for (let i = startPage; i < endPage; i++) {
            paginationItems.push(
                <Pagination.Item
                    key={i}
                    onClick={() => handlePageChange(i)}
                    active={activePage === i}>
                    {i + 1}
                </Pagination.Item>
            )
        }

        // Add ending pages or ellipsis
        if (totalPages - activePage > pagesToShow) {
            paginationItems.push(ellipsis)
            for (let i = totalPages - tailPagesToShow; i < totalPages; i++) {
                paginationItems.push(
                    <Pagination.Item
                        key={i}
                        onClick={() => handlePageChange(i)}
                        active={activePage === i}>
                        {i + 1}
                    </Pagination.Item>
                )
            }
        }

        // Add Next and Last Buttons
        paginationItems.push(
            <Pagination.Next
                key="next"
                onClick={() => handlePageChange(activePage + 1)}
                disabled={activePage === totalPages - 1}
            />
        )
        paginationItems.push(
            <Pagination.Last
                key="last"
                onClick={() => handlePageChange(totalPages - 1)}
                disabled={activePage === totalPages - 1}
            />
        )

        return paginationItems
    }

    return (
        <div css={componentStyle}>
            <Loader show={fetchStatus === "loading"} />

            <h2>All Applications</h2>

            <Form.Label className="h6 mt-4">Search</Form.Label>
            <div className="d-flex">
                <div className="col-sm-4">
                    <Form.Control
                        type="text"
                        className="form-control-sm"
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <div className="mx-4 col-3">
                    <button
                        type="button"
                        onClick={() => {
                            search && handleSearch()
                        }}
                        className="btn-sm select btn btn-primary">
                        Search
                    </button>
                    <button
                        type="button"
                        onClick={() => getApplicationsList()}
                        className="mx-2 btn-sm select btn btn-primary">
                        View All
                    </button>
                </div>
            </div>

            {applicationList?.length > 0 && (
                <>
                    <Table
                        striped
                        bordered
                        hover
                        responsive
                        className="centered-row mt-4">
                        <thead>
                            <tr>
                                <th>ID Number</th>
                                <th>#</th>
                                <th>First Named Insured</th>
                                <th>Agency Name</th>
                                <th>Agency Contact</th>
                                <th>Date Started</th>
                                <th>Date Submitted</th>
                                <th>Status</th>
                                <th>Application</th>

                                <th>View Comments</th>
                                <th>Bind Request Date</th>
                                <th>Actions</th>
                                <th>Quote PDF</th>
                                <th>BOR Transfer</th>
                            </tr>
                        </thead>
                        <tbody>
                            {applicationList?.map((application, index) => {
                                return (
                                    <tr key={index}>
                                        <td>{application?.friendlyId}</td>
                                        <td>{application?.customerNumber || ""}</td>
                                        <td>{application?.firstNamedInsured}</td>
                                        <td>
                                            {application?.agencyName}
                                            {application?.agencyName && (
                                                <FontAwesomeIcon
                                                    className="mx-2 cursor-pointer"
                                                    icon={byPrefixAndName.fal["circle-info"]}
                                                    onClick={() => {
                                                        handleInformationShow("Agency", application?.agencyId)
                                                    }}
                                                />
                                            )}
                                        </td>
                                        <td>
                                            {application?.agencyContact}
                                            {application?.agencyContact && (
                                                <FontAwesomeIcon
                                                    className="mx-2 cursor-pointer"
                                                    icon={byPrefixAndName.fal["circle-info"]}
                                                    onClick={() =>
                                                        handleInformationShow("Contact", application?.agencyContactId)
                                                    }
                                                />
                                            )}
                                        </td>
                                        <td>{formatDate(application?.dateStarted)}</td>
                                        <td>{formatDate(application?.dateSubmitted)}</td>
                                        <td>{camelCaseToWords(application?.status)}</td>
                                        <td style={{ textAlign: "center" }}>
                                            <Button
                                                onClick={() => handleOpenModal(application?.id)}
                                                variant="light"
                                                className="me-2 btn-sm">
                                                <FontAwesomeIcon icon={byPrefixAndName.fal["magnifying-glass"]} />
                                            </Button>
                                        </td>

                                        <td
                                            onClick={() => handleComments(application?.id)}
                                            className="text-primary h6"
                                            role="button">
                                            Comments
                                        </td>
                                        <td>
                                            {application?.bindEffectiveDate
                                                ? formatDate(application?.bindEffectiveDate)
                                                : "N/A"}
                                        </td>
                                        <td className="text-center">
                                            {application?.status !== "Approved" && role === "SuperAdmin" && (
                                                <Button
                                                    variant="light"
                                                    className="me-2 btn-sm"
                                                    onClick={() => {
                                                        handleNavigatorState(application?.id)
                                                    }}>
                                                    <FontAwesomeIcon icon={byPrefixAndName.fal["pen-to-square"]} />
                                                </Button>
                                            )}
                                        </td>
                                        <td className="text-center">
                                            <Button
                                                variant="light"
                                                className="me-2 btn-sm"
                                                onClick={() => downloadApplicationQuote(application?.id)}>
                                                <FontAwesomeIcon icon={byPrefixAndName.fal["download"]} />
                                            </Button>
                                        </td>
                                        <td className="text-center">
                                            <Button
                                                variant="light"
                                                className="me-2 btn-sm"
                                                onClick={(e) => handleBORModal(application?.id)}>
                                                <FontAwesomeIcon icon={byPrefixAndName.fal["share"]} />
                                            </Button>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </Table>
                    {totalPages > 1 && (
                        <Pagination
                            className="mt-4 d-flex justify-content-end"
                            size="md">
                            {renderPagination()}
                        </Pagination>
                    )}
                </>
            )}
        </div>
    )
}

export default AllApplications
